import { useEffect } from "react";
import { AdminBookingFull } from "@/mocks/admin";
import StatusMenu from "./StatusMenu";

type Props = {
  booking: AdminBookingFull | null;
  onClose: () => void;
  onChangeStatus: (next: AdminBookingFull["status"]) => void;
  onAssignClick: () => void;
};

const paymentTint: Record<string, string> = {
  paid: "bg-primary-100 text-primary-800",
  due: "bg-accent-100 text-accent-800",
  refunded: "bg-background-200 text-foreground-700",
};

export default function BookingDrawer({
  booking,
  onClose,
  onChangeStatus,
  onAssignClick,
}: Props) {
  useEffect(() => {
    document.body.style.overflow = booking ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [booking]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && booking) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [booking, onClose]);

  if (!booking) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-background-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 md:px-6 py-4 md:py-5 border-b border-background-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                {booking.id}
              </p>
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mt-0.5">
                {booking.service}
              </h3>
              <p className="text-xs text-foreground-600 mt-1">
                {booking.dateLabel} · {booking.time} · {booking.duration}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer flex-shrink-0"
              aria-label="Close"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="mt-4 flex items-center justify-between gap-2">
            <StatusMenu
              status={booking.status}
              onChange={onChangeStatus}
            />
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${paymentTint[booking.payment]}`}
            >
              <i className="ri-bank-card-line text-xs"></i>
              {booking.payment === "paid"
                ? "Paid"
                : booking.payment === "due"
                ? "Payment due"
                : "Refunded"}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-5">
          {/* Customer */}
          <section>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground-500 mb-2">
              Customer
            </p>
            <div className="flex items-center gap-3">
              <img
                src={booking.customerAvatar}
                alt={booking.customer}
                className="w-12 h-12 rounded-full object-cover object-top"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-950 truncate">
                  {booking.customer}
                </p>
                <p className="text-xs text-foreground-600 truncate">
                  {booking.customerEmail}
                </p>
                <p className="text-xs text-foreground-600">{booking.customerPhone}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-background-100 hover:bg-background-200 text-foreground-800 whitespace-nowrap cursor-pointer">
                <i className="ri-message-3-line"></i> Message
              </button>
              <button className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-background-100 hover:bg-background-200 text-foreground-800 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i> Call
              </button>
            </div>
          </section>

          {/* Cleaner */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground-500">
                Assigned cleaner
              </p>
              <button
                onClick={onAssignClick}
                className="text-xs font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
              >
                {booking.cleanerId ? "Reassign" : "Assign"}
              </button>
            </div>
            {booking.cleanerId && booking.cleanerAvatar ? (
              <div className="flex items-center gap-3 p-3 rounded-xl border border-background-200 bg-background-100/50">
                <img
                  src={booking.cleanerAvatar}
                  alt={booking.cleanerName || ""}
                  className="w-11 h-11 rounded-full object-cover object-top"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 truncate">
                    {booking.cleanerName}
                  </p>
                  <p className="text-[11px] text-foreground-600">
                    Confirmed · ETA {booking.time}
                  </p>
                </div>
              </div>
            ) : (
              <button
                onClick={onAssignClick}
                className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-accent-300 bg-accent-50/40 text-left hover:bg-accent-50/70 cursor-pointer"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-user-add-line text-lg"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-accent-900">
                    No cleaner assigned
                  </p>
                  <p className="text-[11px] text-accent-700">
                    Pick someone from the roster
                  </p>
                </div>
              </button>
            )}
          </section>

          {/* Job details */}
          <section>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground-500 mb-2">
              Job details
            </p>
            <div className="rounded-xl border border-background-200 divide-y divide-background-200 text-sm">
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-foreground-600">Address</span>
                <span className="text-foreground-900 font-medium text-right">
                  {booking.address}
                  <br />
                  <span className="text-xs text-foreground-600">{booking.city}</span>
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-foreground-600">Home size</span>
                <span className="text-foreground-900 font-medium">
                  {booking.bedrooms} bd · {booking.bathrooms} ba
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-foreground-600">Duration</span>
                <span className="text-foreground-900 font-medium">{booking.duration}</span>
              </div>
              {booking.extras.length > 0 && (
                <div className="px-4 py-2.5">
                  <span className="text-xs text-foreground-600">Extras</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {booking.extras.map((e) => (
                      <span
                        key={e}
                        className="text-[11px] bg-secondary-100 text-secondary-800 px-2 py-0.5 rounded-full"
                      >
                        {e}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-foreground-600">Booked on</span>
                <span className="text-foreground-900 font-medium">{booking.createdAt}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-foreground-600">Total</span>
                <span className="text-foreground-950 font-heading font-semibold">
                  ${booking.amount}
                </span>
              </div>
            </div>
          </section>

          {/* Notes */}
          {booking.notes && (
            <section>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground-500 mb-2">
                Notes
              </p>
              <p className="text-xs text-foreground-700 bg-background-100/60 border border-background-200 rounded-xl p-3 leading-relaxed">
                {booking.notes}
              </p>
            </section>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 md:px-6 py-4 border-t border-background-200 bg-background-100/40">
          <div className="grid grid-cols-3 gap-2">
            <button className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-background-50 border border-background-200 text-foreground-800 hover:bg-background-100 whitespace-nowrap cursor-pointer">
              <i className="ri-calendar-line"></i> Reschedule
            </button>
            <button className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-background-50 border border-background-200 text-foreground-800 hover:bg-background-100 whitespace-nowrap cursor-pointer">
              <i className="ri-refund-2-line"></i> Refund
            </button>
            <button
              onClick={() => onChangeStatus("cancelled")}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-background-50 border border-accent-200 text-accent-800 hover:bg-accent-50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-close-circle-line"></i> Cancel
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}